import { createGaussianData } from "./helpers/GaussianData";
import { createUniformSampler } from "./helpers/GaussianSampling";
import { sigmoid } from "./helpers/BinaryUtils";
import { clamp01, shDCToColor } from "./helpers/ColorUtils";
import { writeNormalizedQuaternionWXYZ } from "./helpers/QuaternionUtils";

const HEADER_SEARCH_LIMIT = 65536;
const DEFAULT_SCALE = 0.01;

/**
 * Decodes a binary little-endian Gaussian PLY file into the shared
 * Gaussian representation.
 *
 * Supports the common 3DGS f_dc, opacity, scale, and rotation
 * properties, with conventional RGB and sensible defaults as fallbacks.
 */
export function loadPLY(buffer, { maxGaussians } = {}) {
  const { dataOffset, sourceCount, properties, propertyMap, stride } =
    parsePLYHeader(buffer);

  const sampler = createUniformSampler(sourceCount, maxGaussians);

  const data = createGaussianData(sampler.outputCount, {
    sourceCount,
    sampled: sampler.sampled,
  });

  const view = new DataView(buffer);

  const hasSHColor = ["f_dc_0", "f_dc_1", "f_dc_2"].every((name) =>
    propertyMap.has(name),
  );

  const hasRGB = ["red", "green", "blue"].every((name) =>
    propertyMap.has(name),
  );

  const hasScale = ["scale_0", "scale_1", "scale_2"].every((name) =>
    propertyMap.has(name),
  );

  const hasRotation = ["rot_0", "rot_1", "rot_2", "rot_3"].every((name) =>
    propertyMap.has(name),
  );

  for (let outputIndex = 0; outputIndex < data.count; outputIndex++) {
    const sourceIndex = sampler.sourceIndexFor(outputIndex);

    const base = dataOffset + sourceIndex * stride;

    const read = (name) => readProperty(view, base, propertyMap.get(name));

    const offset = outputIndex * 3;

    data.positions[offset] = read("x");
    data.positions[offset + 1] = read("y");
    data.positions[offset + 2] = read("z");

    if (hasSHColor) {
      data.colors[offset] = shDCToColor(read("f_dc_0"));

      data.colors[offset + 1] = shDCToColor(read("f_dc_1"));

      data.colors[offset + 2] = shDCToColor(read("f_dc_2"));
    } else if (hasRGB) {
      data.colors[offset] = clamp01(read("red") / 255);

      data.colors[offset + 1] = clamp01(read("green") / 255);

      data.colors[offset + 2] = clamp01(read("blue") / 255);
    } else {
      data.colors[offset] = 1;
      data.colors[offset + 1] = 1;
      data.colors[offset + 2] = 1;
    }

    data.opacities[outputIndex] = propertyMap.has("opacity")
      ? sigmoid(read("opacity"))
      : 1;

    if (hasScale) {
      data.scales[offset] = Math.exp(read("scale_0"));

      data.scales[offset + 1] = Math.exp(read("scale_1"));

      data.scales[offset + 2] = Math.exp(read("scale_2"));
    } else {
      data.scales[offset] = DEFAULT_SCALE;
      data.scales[offset + 1] = DEFAULT_SCALE;
      data.scales[offset + 2] = DEFAULT_SCALE;
    }

    if (hasRotation) {
      writeNormalizedQuaternionWXYZ(
        data.rotations,
        outputIndex,
        read("rot_0"),
        read("rot_1"),
        read("rot_2"),
        read("rot_3"),
      );
    } else {
      writeNormalizedQuaternionWXYZ(data.rotations, outputIndex, 1, 0, 0, 0);
    }
  }

  return data;
}

/**
 * Parses the PLY header and calculates the byte layout of each
 * vertex property.
 */
function parsePLYHeader(buffer) {
  const bytes = new Uint8Array(buffer);
  const decoder = new TextDecoder("ascii");

  const searchLength = Math.min(bytes.length, HEADER_SEARCH_LIMIT);

  const headerSearch = decoder.decode(bytes.subarray(0, searchLength));

  const marker = "end_header";
  const markerIndex = headerSearch.indexOf(marker);

  if (markerIndex === -1) {
    throw new Error("Invalid PLY: end_header not found.");
  }

  let dataOffset = markerIndex + marker.length;

  while (
    dataOffset < bytes.length &&
    (bytes[dataOffset] === 10 || bytes[dataOffset] === 13)
  ) {
    dataOffset++;
  }

  const lines = decoder
    .decode(bytes.subarray(0, dataOffset))
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const formatLine = lines.find((line) => line.startsWith("format "));

  if (!formatLine?.includes("binary_little_endian")) {
    throw new Error(
      "Only binary_little_endian Gaussian PLY files are currently supported.",
    );
  }

  const vertexLine = lines.find((line) => line.startsWith("element vertex "));

  if (!vertexLine) {
    throw new Error("PLY contains no vertex element.");
  }

  const sourceCount = Number(vertexLine.split(/\s+/)[2]);

  if (!Number.isInteger(sourceCount) || sourceCount <= 0) {
    throw new Error("Invalid PLY vertex count.");
  }

  const properties = [];
  let inVertex = false;

  for (const line of lines) {
    if (line.startsWith("element ")) {
      inVertex = line.startsWith("element vertex ");
      continue;
    }

    if (!inVertex || !line.startsWith("property ")) {
      continue;
    }

    const parts = line.split(/\s+/);

    if (parts[1] === "list") {
      throw new Error("PLY vertex list properties are unsupported.");
    }

    properties.push({
      type: parts[1],
      name: parts[2],
    });
  }

  let stride = 0;

  for (const property of properties) {
    property.offset = stride;
    property.size = typeSize(property.type);
    stride += property.size;
  }

  const propertyMap = new Map(
    properties.map((property) => [property.name, property]),
  );

  for (const name of ["x", "y", "z"]) {
    if (!propertyMap.has(name)) {
      throw new Error(`PLY missing required property: ${name}`);
    }
  }

  return {
    dataOffset,
    sourceCount,
    properties,
    propertyMap,
    stride,
  };
}

/**
 * Returns the byte width of a supported scalar PLY property type.
 */
function typeSize(type) {
  switch (type) {
    case "char":
    case "int8":
    case "uchar":
    case "uint8":
      return 1;

    case "short":
    case "int16":
    case "ushort":
    case "uint16":
      return 2;

    case "int":
    case "int32":
    case "uint":
    case "uint32":
    case "float":
    case "float32":
      return 4;

    case "double":
    case "float64":
      return 8;

    default:
      throw new Error(`Unsupported PLY type: ${type}`);
  }
}

/**
 * Reads one scalar PLY property from a vertex record.
 */
function readProperty(view, base, property) {
  if (!property) {
    return undefined;
  }

  const offset = base + property.offset;

  switch (property.type) {
    case "char":
    case "int8":
      return view.getInt8(offset);

    case "uchar":
    case "uint8":
      return view.getUint8(offset);

    case "short":
    case "int16":
      return view.getInt16(offset, true);

    case "ushort":
    case "uint16":
      return view.getUint16(offset, true);

    case "int":
    case "int32":
      return view.getInt32(offset, true);

    case "uint":
    case "uint32":
      return view.getUint32(offset, true);

    case "float":
    case "float32":
      return view.getFloat32(offset, true);

    case "double":
    case "float64":
      return view.getFloat64(offset, true);

    default:
      throw new Error(`Unsupported PLY type: ${property.type}`);
  }
}
